import { useCallback, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  ATTEMPT_DERIVED_STATUS,
  CALENDAR_TYPES,
  EMAIL_ENTITY_TYPE,
  PLATFORM_MODULES,
} from "@miplatform/constants";
import { getTotalMilliseconds } from "@miplatform/utils";
import { EmailFormDrawer } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { Holds } from "../Holds/Holds";
import { useHoldsViewModel } from "../Holds/useHoldsViewModel";
import { CloseoutDrawer } from "./CloseoutDrawer/CloseoutDrawer";
import { DispatchHeader } from "./DispatchHeader/DispatchHeader";
import { DispatchScheduler } from "./DispatchScheduler";
import { TaskCard } from "./TaskCard/TaskCard";
import { ViewConfigProvider } from "./ViewConfigContext";
import { useSchedulerController } from "./useSchedulerController";

export function DispatchCalendarRenderer({
  config: viewConfig = {},
  loading,
  onRefresh,
  tabs,
  handleActiveTabChange,
  updateCalendarCounts,
  activeTab,
  calendarSet,
}) {
  const {
    config,
    users,
    data,
    displayData,
    buckets,
    dateFilterItem,
    date,
    dateRange,
    selectedTechnician,
    scheduleView,
    calendarView,
    time,
    modal,
    isDragging,
    emailModal,
    emailDrawersMap,
    initialLoading,
    isSignalRConnected,
    MODALS,
    isCreateNewOrderDrawerOpen,
    isTechnicianDrawerOpen,
    selectedBoard,
    boardUsers,
    isBoardModalOpen,
    selectedUserId,
    carouselMode,
    carouselInterval,
    isCarouselPaused,
    attemptsLoading,
    closeoutDrawersMap,
    minimizedStatusMap,
    searchedAttemptId,
    hasModalOpen,
    updateUsersOnBoard,
    onSelectedTechnicianChange,
    setHasModalOpen,
    updateBoardConfig,
    appendOrderTypeColor,
    handleOpenCloseoutDrawer,
    handleEmailDrawerOpen,
    handleEmailModalOpen,
    handleCloseCloseoutDrawer,
    handleCloseAllCloseoutDrawers,
    handleMinimizeCloseoutDrawer,
    handleCloseCreateNewOrderDrawer,
    handleEmailModalClose,
    handleEmailDrawerClose,
    onCardHover,
    onChangePrevBoard,
    onChangeNextBoard,
    setCarouselInterval,
    toggleCarouselMode,
    setSelectedUserId,
    handleBoardModalOpen,
    handleBoardModalClose,
    setSelectedBoard,
    handleTechnicianDrawerClose,
    setIsTechnicianDrawerOpen,
    setIsCreateNewOrderDrawerOpen,
    updateTime,
    setDate,
    loadSchedulerConfig,
    loadAppointments,
    onDateChange,
    onEventUpdate,
    onEventCreate,
    onColumnHeaderClick,
    setScheduleView,
    setCalendarView,
    processData,
    startConnectionAndSubscribe,
    loadBuckets,
    closeModal,
    onDrag,
    handleAutoLockAction,
    onAttemptUpdated,
    onSearchSelect,
    loadUsers,
  } = useSchedulerController({ viewConfig, onRefresh, calendarSet });

  const bucketsForTab = buckets?.[viewConfig.id] || [];
  const {
    isLoading: isHoldsLoading,
    holdsData,
    getHoldsData,
  } = useHoldsViewModel({
    appendOrderTypeColor,
    viewConfig,
  });

  useEffect(() => {
    handleCloseAllCloseoutDrawers();
  }, [activeTab]);

  useEffect(() => {
    if (viewConfig.trays?.length) {
      loadBuckets(viewConfig);
    }
  }, [activeTab, date, dateRange]);

  useEffect(() => {
    if (viewConfig.trays?.length || viewConfig.serviceAreas?.length) {
      startConnectionAndSubscribe();
    }
  }, [activeTab, viewConfig?.trays, viewConfig?.serviceAreas]);

  useEffect(() => {
    if (viewConfig.trays?.length) {
      const intervalId = setInterval(() => {
        if (!isDragging) {
          // TODO : see if we need to pause signalr updates while dragging
          loadBuckets(viewConfig);
        }
      }, getTotalMilliseconds(calendarSet.pollingPeriodSeconds || 5));

      return () => {
        clearInterval(intervalId);
      };
    }
    return undefined;
  }, [isDragging, config, activeTab, date, dateRange, loadBuckets]);

  const { classes } = useStyles();

  const TaskCardPlus = useCallback(
    (props) => (
      <TaskCard
        {...props}
        isMinimized={
          closeoutDrawersMap[props.event?.id]?.wasMinimized &&
          !closeoutDrawersMap[props.event?.id]?.isOpen
        }
        setHasModalOpen={setHasModalOpen}
        onAttemptUpdated={onAttemptUpdated}
        handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
        onEmailDrawerOpen={handleEmailDrawerOpen}
        onEmailModalOpen={handleEmailModalOpen}
        updateCalendarCounts={updateCalendarCounts}
        searchedAttemptId={searchedAttemptId}
        calendarSet={calendarSet}
      />
    ),
    [closeoutDrawersMap, emailDrawersMap, searchedAttemptId]
  );

  return (
    <ViewConfigProvider viewConfig={viewConfig} calendarSet={calendarSet}>
      <Box className={classes.root} data-testid="scheduler">
        <DispatchHeader
          isSignalRConnected={isSignalRConnected}
          tabs={tabs}
          holdsData={holdsData}
          calendarView={calendarView}
          date={date}
          setDate={setDate}
          onDateChange={onDateChange}
          dateRange={dateRange}
          activeTab={activeTab}
          setActiveTab={handleActiveTabChange}
          scheduleView={scheduleView}
          setScheduleView={setScheduleView}
          setCalendarView={setCalendarView}
          isLoading={loading || initialLoading || isHoldsLoading}
          setIsCreateNewOrderDrawerOpen={setIsCreateNewOrderDrawerOpen}
          appendOrderTypeColor={appendOrderTypeColor}
          viewConfig={viewConfig}
          buckets={bucketsForTab}
          onSearchSelect={onSearchSelect}
        />
        <Box className={classes.content}>
          {viewConfig.calendarType === CALENDAR_TYPES.SCHEDULER && (
            <DispatchScheduler
              date={date}
              time={time}
              buckets={bucketsForTab}
              dateRange={dateRange}
              TaskCardPlus={TaskCardPlus}
              onRefresh={onRefresh}
              scheduleView={scheduleView}
              calendarView={calendarView}
              emailDrawersMap={emailDrawersMap}
              hasModalOpen={hasModalOpen}
              config={config}
              viewConfig={viewConfig}
              closeoutDrawersMap={closeoutDrawersMap}
              minimizedStatusMap={minimizedStatusMap}
              selectedBoard={selectedBoard}
              users={users}
              displayData={displayData}
              selectedTechnician={selectedTechnician}
              emailModal={emailModal}
              data={data}
              dateFilterItem={dateFilterItem}
              isCreateNewOrderDrawerOpen={isCreateNewOrderDrawerOpen}
              isTechnicianDrawerOpen={isTechnicianDrawerOpen}
              boardUsers={boardUsers}
              selectedUserId={selectedUserId}
              carouselMode={carouselMode}
              carouselInterval={carouselInterval}
              isCarouselPaused={isCarouselPaused}
              attemptsLoading={attemptsLoading}
              onSelectedTechnicianChange={onSelectedTechnicianChange}
              onAttemptUpdated={onAttemptUpdated}
              updateTime={updateTime}
              loadSchedulerConfig={loadSchedulerConfig}
              loadAppointments={loadAppointments}
              loadUsers={loadUsers}
              processData={processData}
              updateUsersOnBoard={updateUsersOnBoard}
              handleOpenCloseoutDrawer={handleOpenCloseoutDrawer}
              handleEmailDrawerOpen={handleEmailDrawerOpen}
              handleCloseCreateNewOrderDrawer={handleCloseCreateNewOrderDrawer}
              handleEmailModalClose={handleEmailModalClose}
              handleEmailDrawerClose={handleEmailDrawerClose}
              onCardHover={onCardHover}
              onChangePrevBoard={onChangePrevBoard}
              onChangeNextBoard={onChangeNextBoard}
              setCarouselInterval={setCarouselInterval}
              toggleCarouselMode={toggleCarouselMode}
              setSelectedUserId={setSelectedUserId}
              handleBoardModalOpen={handleBoardModalOpen}
              setSelectedBoard={setSelectedBoard}
              handleTechnicianDrawerClose={handleTechnicianDrawerClose}
              setIsTechnicianDrawerOpen={setIsTechnicianDrawerOpen}
              onEventUpdate={onEventUpdate}
              onEventCreate={onEventCreate}
              onColumnHeaderClick={onColumnHeaderClick}
              onDrag={onDrag}
              isBoardModalOpen={isBoardModalOpen}
              MODALS={MODALS}
              modal={modal}
              handleBoardModalClose={handleBoardModalClose}
              updateBoardConfig={updateBoardConfig}
              closeModal={closeModal}
              handleAutoLockAction={handleAutoLockAction}
            />
          )}
          {viewConfig.calendarType === CALENDAR_TYPES.HOLDS_LIST && (
            <Holds
              holdsData={holdsData}
              getHoldsData={getHoldsData}
              updateCalendarCounts={updateCalendarCounts}
              buckets={bucketsForTab}
              EventCard={TaskCardPlus}
              count={
                tabs.find((tab) => tab.value === activeTab)?.chipLabel || "0"
              }
              activeTab={activeTab}
              viewConfig={viewConfig}
              calendarSet={calendarSet}
              onCloseoutDrawerOpen={handleOpenCloseoutDrawer}
              module={PLATFORM_MODULES.MI_DISPATCH}
            />
          )}
          {Object.values(closeoutDrawersMap).map(
            ({ attempt, isOpen, tab, screen, view, wasMinimized }) => (
              <CloseoutDrawer
                key={attempt.id}
                open={isOpen}
                wasMinimized={wasMinimized}
                isCloseoutMinimized={
                  minimizedStatusMap[ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT]
                }
                isPreBillMinimized={
                  minimizedStatusMap[ATTEMPT_DERIVED_STATUS.PENDING_PREBILL]
                }
                listAttempt={attempt}
                tab={tab}
                screen={screen}
                view={view}
                onAttemptUpdated={onAttemptUpdated}
                onClose={handleCloseCloseoutDrawer}
                onMinimize={handleMinimizeCloseoutDrawer}
                onEmailDrawerOpen={handleEmailDrawerOpen}
              />
            )
          )}
          {Object.values(emailDrawersMap).map(
            ({ attempt, isOpen, templateType }) => (
              <EmailFormDrawer
                key={`${attempt.id}-${templateType}`}
                open={isOpen}
                entity={attempt}
                entityType={EMAIL_ENTITY_TYPE.ATTEMPT}
                templateType={templateType}
                onCloseoutDrawerOpen={handleOpenCloseoutDrawer}
                onClose={handleEmailDrawerClose}
              />
            )
          )}
        </Box>
      </Box>
    </ViewConfigProvider>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
}));
